/**
 * Constants for KLineChart Financial Astrology Implementation
 * @version 1.0.0
 */

// ============================================
// Planet Colors
// ============================================

export const PLANET_COLORS = {
  Sun: '#FFD700',
  Moon: '#C0C0C0',
  Mercury: '#87CEEB',
  Venus: '#FFC0CB',
  Mars: '#FF4500',
  Jupiter: '#FF8C00',
  Saturn: '#8B4513',
  Uranus: '#00CED1',
  Neptune: '#4169E1',
  Pluto: '#800080',
  NorthNode: '#228B22',
  SouthNode: '#DC143C',
  Chiron: '#9932CC',
  // Alternative names (lowercase)
  sun: '#FFD700',
  moon: '#C0C0C0',
  mercury: '#87CEEB',
  venus: '#FFC0CB',
  mars: '#FF4500',
  jupiter: '#FF8C00',
  saturn: '#8B4513',
  uranus: '#00CED1',
  neptune: '#4169E1',
  pluto: '#800080',
};

// ============================================
// Model Types
// ============================================

export const MODEL_TYPES = {
  geo: "geocentric",
  helio: "heliocentric"
}

// ============================================
// Trendline Colors
// ============================================

export const TRENDLINE_COLORS = {
  geo: "#2962FF",
  helio: "#00d3a1",
}

// ============================================
// Aspect Degrees
// ============================================

export const ASPECT_DEGREES = {
  conjunction: 0,
  opposition: 180,
  square: 90,
  trine: 120,
  sextile: 60,
  inconjunct: 150,
  semisextile: 30,
  semisquare: 45,
  sesquiquadrate: 135,
  quintile: 72,
  biquintile: 144,
  semiquintile: 36,
};

// ============================================
// Chart Style Configuration
// ============================================

export const DEFAULT_CHART_STYLES = {
  grid: {
    show: true,
    horizontal: {
      show: true,
      size: 1,
      color: '#EDEDED',
      style: 'dashed',
      dashedValue: [2, 2],
    },
    vertical: {
      show: true,
      size: 1,
      color: '#EDEDED',
      style: 'dashed',
      dashedValue: [2, 2],
    },
  },
  candle: {
    type: 'candle_solid',
    bar: {
      upColor: '#2DC08E',
      downColor: '#F92855',
      noChangeColor: 'transparent',
      upBorderColor: '#2DC08E',
      downBorderColor: '#F92855',
      noChangeBorderColor: 'transparent',
      upWickColor: '#2DC08E',
      downWickColor: '#F92855',
      noChangeWickColor: 'transparent',
    },
    priceMark: {
      show: false,
      high: {
        show: true,
        color: '#000',
        textSize: 10,
      },
      low: {
        show: true,
        color: '#000',
        textSize: 10,
      },
      last: {
        show: true,
        upColor: '#2DC08E',
        downColor: '#F92855',
        noChangeColor: '#888888',
        line: {
          show: true,
          style: 'dashed',
          dashedValue: [4, 4],
          size: 1,
        },
        text: {
          show: true,
          size: 12,
          color: '#000',
        },
      },
    },
    tooltip: {
      showRule: 'always',
      showType: 'standard',
    },
  },
  crosshair: {
    show: true,
    horizontal: {
      show: true,
      line: {
        show: true,
        style: 'dashed',
        dashedValue: [4, 2],
        size: 1,
        color: '#888888',
      },
      text: {
        show: true,
        style: 'fill',
        color: '#fff',
        size: 12,
        backgroundColor: '#000',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
    vertical: {
      show: true,
      line: {
        show: true,
        style: 'dashed',
        dashedValue: [4, 2],
        size: 1,
        color: '#888888',
      },
      text: {
        show: true,
        style: 'fill',
        color: '#fff',
        size: 12,
        backgroundColor: '#000',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },
  xAxis: {
    show: true,
    axisLine: {
      show: true,
      color: '#888888',
      size: 1,
    },
    tickText: {
      show: true,
      color: '#000',
      size: 12,
    },
    tickLine: {
      show: true,
      size: 1,
      length: 3,
      color: '#888888',
    },
  },
  yAxis: {
    show: true,
    axisLine: {
      show: true,
      color: '#888888',
      size: 1,
    },
    tickText: {
      show: true,
      color: '#000',
      size: 12,
    },
    tickLine: {
      show: true,
      size: 1,
      length: 3,
      color: '#888888',
    },
  },
  separator: {
    size: 1,
    color: '#888888',
    fill: true,
    activeBackgroundColor: 'rgba(230, 230, 230, .15)',
  },
  overlay: {
    point: {
      color: '#1677FF',
      borderColor: 'rgba(22, 119, 255, 0.35)',
      borderSize: 1,
      radius: 5,
      activeColor: '#1677FF',
      activeBorderColor: 'rgba(22, 119, 255, 0.35)',
      activeBorderSize: 3,
      activeRadius: 5,
    },
    line: {
      style: 'solid',
      smooth: false,
      color: '#1677FF',
      size: 1,
      dashedValue: [2, 2],
    },
  },
};

// ============================================
// Line Style Constants
// ============================================

export const LINE_STYLES = {
  SOLID: 0,
  DASHED: 1,
};

// ============================================
// Default Configuration
// ============================================

export const DEFAULT_CONFIG = {
  // Maximum iterations for drawing repeated planet lines
  MAX_LINE_ITERATIONS: 99,
  
  // Time threshold for tooltip matching (seconds)
  // TOOLTIP_TIME_THRESHOLD: 60 * 60, // 1 hour

  TOOLTIP_TIME_THRESHOLD: 86400,
  
  // Price threshold for tooltip matching
  TOOLTIP_PRICE_THRESHOLD: 10,
  
  // Level of Detail - max lines to render
  LOD_MAX_LINES: 50,
  
  // Interpolation points for smooth curves
  INTERPOLATION_POINTS: 8,
  
  // Bezier tension for smooth curves
  BEZIER_TENSION: 0.4,
  
  // Days to extend chart range for drawing
  CHART_RANGE_EXTENSION_DAYS: 15,
  
  // Price range extension percentage
  PRICE_RANGE_EXTENSION: 0.2,
};

export const PREDICTION_LINE_CONFIG = {
  // Line styling (matches TradingView overrides)
  LINE_COLOR: '#2962FF',
  LINE_WIDTH: 3,
  
  // Marker styling (matches TradingView icon: 0xf111)
  MARKER_COLOR: '#2962FF',
  MARKER_RADIUS: 5,
  MARKER_BORDER_COLOR: '#FFFFFF',
  MARKER_BORDER_WIDTH: 0,
  
  // Show markers only when zoomed in (visibleDaysRange <= 100)
  // This matches TradingView's behavior
  SHOW_MARKERS_DAYS_THRESHOLD: 100,
  
  // Extension for filtering points
  CHART_RANGE_EXTENSION_DAYS: 15,
};

// ============================================
// Event Types
// ============================================

export const CHART_EVENTS = {
  ON_DATA_READY: 'onDataReady',
  ON_ZOOM: 'onZoom',
  ON_SCROLL: 'onScroll',
  ON_VISIBLE_RANGE_CHANGE: 'onVisibleRangeChange',
  ON_CROSSHAIR_CHANGE: 'onCrosshairChange',
  ON_CANDLE_BAR_CLICK: 'onCandleBarClick',
  ON_TOOLTIP_ICON_CLICK: 'onTooltipIconClick',
  ON_PANE_DRAG: 'onPaneDrag',
};

// ============================================
// Overlay Names
// ============================================

export const OVERLAY_NAMES = {
  PLANET_LINE: 'planetLine',
  PLANET_LINE_CANVAS: 'planetLineCanvas',
  TREND_LINE: 'predictionLine',
  TREND_LINE_CANVAS: 'predictionLineCanvas',
  PRICE_RANGE: 'priceRange',             // <-- ADD (figure-based version)
  PRICE_RANGE_CANVAS: 'priceRangeCanvas', // <-- ADD (canvas version)
};
